import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

type ColumnRef = { id: string; name: string };

type Props = {
  columnOrder: string[];
  columnsById: Record<string, ColumnRef>;
  currentColumnId: string;
  onSelectColumn: (columnId: string) => void;
  disabled?: boolean;
};

/**
 * Pipedrive-style chevron stage journey.
 * Stages up to and including the current one are green; click moves the card.
 */
export default function StageJourneyBar({
  columnOrder,
  columnsById,
  currentColumnId,
  onSelectColumn,
  disabled = false,
}: Props) {
  const { t } = useTranslation();
  const currentIndex = Math.max(0, columnOrder.indexOf(currentColumnId));
  const barRef = useRef<HTMLDivElement>(null);
  const currentStepRef = useRef<HTMLButtonElement>(null);

  // On a narrow screen the bar scrolls sideways; bring the current stage to the middle of it.
  useEffect(() => {
    const bar = barRef.current;
    const step = currentStepRef.current;
    if (!bar || !step) return;
    const barBox = bar.getBoundingClientRect();
    const stepBox = step.getBoundingClientRect();
    bar.scrollLeft += stepBox.left - barBox.left - (barBox.width - stepBox.width) / 2;
  }, [currentColumnId]);

  if (!columnOrder.length) return null;

  return (
    <div ref={barRef} data-testid="stage-journey-bar" className="stage-journey-bar w-full overflow-x-auto border-b border-[var(--p-color-border)] bg-[var(--p-color-bg-surface)] px-4 py-3">
      <div
        className="flex min-w-min items-stretch"
        role="list"
        aria-label={t("project_stage_journey", {
          defaultValue: "Pipeline stages",
        })}
      >
        {columnOrder.map((colId, index) => {
          const col = columnsById[colId];
          const name = col?.name || colId;
          const isPastOrCurrent = index <= currentIndex;
          const isCurrent = colId === currentColumnId;
          const isFirst = index === 0;
          const isLast = index === columnOrder.length - 1;

          return (
            <button
              key={colId}
              ref={isCurrent ? currentStepRef : undefined}
              type="button"
              role="listitem"
              disabled={disabled || isCurrent}
              title={name}
              onClick={() => {
                if (!disabled && !isCurrent) onSelectColumn(colId);
              }}
              className={`stage-journey-bar__step relative flex h-9 max-md:h-11 min-w-[7.5rem] flex-1 items-center justify-center px-4 text-center text-xs font-semibold transition-colors ${
                isPastOrCurrent
                  ? "bg-[var(--p-color-bg-fill-success)] text-[var(--p-color-text-success-on-bg-fill)]"
                  : "bg-[var(--p-color-bg-surface-secondary)] text-[var(--p-color-text-secondary)]"
              } ${
                disabled || isCurrent
                  ? "cursor-default"
                  : "cursor-pointer hover:brightness-95"
              } ${isCurrent ? "ring-2 ring-inset ring-[var(--p-color-border-focus)]" : ""}`}
              style={{
                clipPath: isFirst
                  ? isLast
                    ? undefined
                    : "polygon(0 0, calc(100% - 10px) 0, 100% 50%, calc(100% - 10px) 100%, 0 100%)"
                  : isLast
                    ? "polygon(0 0, 100% 0, 100% 100%, 0 100%, 10px 50%)"
                    : "polygon(0 0, calc(100% - 10px) 0, 100% 50%, calc(100% - 10px) 100%, 0 100%, 10px 50%)",
                marginLeft: isFirst ? 0 : -8,
                zIndex: columnOrder.length - index,
              }}
            >
              <span className="truncate px-1">
                {isCurrent
                  ? t("project_stage_current", {
                      defaultValue: "{{name}}",
                      name,
                    })
                  : name}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
